import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUser, fetchUserIdentity } from "../api/queries";
import { safeSessionStorage } from "../utils/storage";

export default function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  let isIntercomUpdated = false;

  const { data: userData, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    enabled: !!user,
  });

  const { data: userIdentity } = useQuery({
    queryKey: ["userIdentity"],
    queryFn: fetchUserIdentity,
    enabled: !!user,
  });

  useEffect(() => {
    // Store intended path when redirecting to TOS
    if (
      user &&
      userData?.is_tos_accepted === false &&
      location.pathname !== "/terms-of-service"
    ) {
      safeSessionStorage.set("intendedPath", location.pathname);
    }

    if (userIdentity) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "user_loaded",
        user_id: userIdentity.id,
        intercom_user_hash: userIdentity.intercom_user_hash,
      });
    }

    if (userData && userIdentity && window.Intercom && !isIntercomUpdated) {
      isIntercomUpdated = true;
      window.Intercom("update", {
        user_id: userData.id,
        user_hash: userIdentity.intercom_user_hash,
        email: userData.email,
        name: userData.name,
        store_id: userIdentity.store_id,
        store_name: userIdentity.store_name,
        trial_end_date: userIdentity.trial_end_date,
        member_count_milestone: userIdentity.member_count_milestone,
        setup_step: userIdentity.setup_step,
        first_scanned_at: userIdentity.first_scanned_at,
      });
    }
  }, [user, userData, location.pathname, userIdentity, window.Intercom]);

  if (!user) {
    // pathname + search, not pathname alone: a deep link's query string is
    // part of the destination (?support=chat opens the Intercom composer once
    // Login sends them back here), and dropping it loses that intent.
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  // Check if TOS is not accepted
  if (userData.is_tos_accepted === false) {
    // If already on TOS page, show it
    if (location.pathname === "/terms-of-service") {
      return children;
    }
    // Otherwise redirect to TOS page
    return <Navigate to="/terms-of-service" />;
  }

  // If we're on the TOS page but TOS is accepted, redirect to dashboard
  if (
    location.pathname === "/terms-of-service" &&
    userData.is_tos_accepted !== false
  ) {
    return <Navigate to="/dashboard" />;
  }

  // If we got here, TOS is accepted or not required
  return children;
}
